import { isShopify } from '@sparklayer-io/spark-dash-core/constants/platform';
import type { Env } from '@sparklayer-io/spark-dash-core/context/env-context';
import type { ApiHandlerContract } from '@sparklayer-io/spark-dash-core/types/handlers/api';
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import type { SessionStores } from './session';

type Setting = {
  key: string;
  value: unknown;
};

// Shopify-specific settings use a different endpoint from the general ones.
const SHOPIFY_SETTING_KEYS = new Set([
  'draftOrdersAutoComplete',
  'draftOrdersAutoCompleteAccountSetAsPaid',
  'draftOrdersAutoCompleteInvoiceSetAsPaid',
  'draftOrdersPriceOverrideEnabled',
  'createDraftOrderOnCalculate',
  'disableNewCustomerInvitation',
  'ignoreTransactions',
  'shippingAutoSelectionStrategy',
]);

export type SettingsState = {
  settingsLoading: boolean;
  settingsData: Setting[];
  getSetting<T = string>(key: string): T | undefined;
  loadSettings(envData?: Env): Promise<void>;
  saveSetting(key: string, value: unknown): Promise<void>;
  saveSettings(settings: Setting[]): Promise<void>;
  saveBaseGroupSetting(key: string, value: unknown): Promise<void>;
  reset(): void;
};

export type SettingsStore = UseBoundStore<StoreApi<SettingsState>>;

export function createSettingsStore(
  apiHandler: ApiHandlerContract,
  stores: SessionStores,
): SettingsStore {
  const initialState = {
    settingsLoading: true,
    settingsData: [] as Setting[],
  } satisfies Partial<SettingsState>;

  return create<SettingsState>()((set, get) => ({
    ...initialState,

    getSetting(key) {
      return get().settingsData.find((s) => s.key === key)?.value as never;
    },

    async loadSettings(envData) {
      set({ settingsLoading: true, settingsData: [] });

      const settingsData =
        (await apiHandler.proxy<Setting[]>({
          method: 'GET',
          path: '/v1/settings',
          payload: {},
        })) ?? [];

      const env = envData ?? stores.useEnvStore.getState().env;
      if (env?.store_id) {
        const shopifySettings = await apiHandler.fetch<Setting[]>({
          method: 'GET',
          path: `/${env.team.slugged_name}/${env.environment}/shopify/settings`,
        });
        settingsData.push(...(shopifySettings ?? []));
      }

      set({ settingsData, settingsLoading: false });
    },

    async saveSetting(key, value) {
      await get().saveSettings([{ key, value }]);
    },

    async saveSettings(settings) {
      if (get().settingsLoading) {
        return;
      }
      set({ settingsLoading: true });

      const env = stores.useEnvStore.getState().env;
      const shopifySettings = settings.filter((s) => SHOPIFY_SETTING_KEYS.has(s.key));
      const generalSettings = settings.filter((s) => !SHOPIFY_SETTING_KEYS.has(s.key));

      if (shopifySettings.length && isShopify(env?.platform)) {
        await apiHandler.fetch({
          method: 'PATCH',
          path: `/${env?.team.slugged_name}/${env?.environment}/shopify/settings`,
          payload: shopifySettings,
        });
      }

      if (generalSettings.length) {
        await apiHandler.proxy({
          method: 'PATCH',
          path: '/v1/settings',
          payload: generalSettings,
        });
      }

      await get().loadSettings();
    },

    async saveBaseGroupSetting(key, value) {
      if (get().settingsLoading) {
        return;
      }
      set({ settingsLoading: true });

      await apiHandler.proxy({
        method: 'PATCH',
        path: '/v1/settings/{slug}',
        params: { slug: 'base' },
        payload: [{ key, value }],
      });

      await get().loadSettings();
    },

    reset() {
      set(initialState);
    },
  }));
}
